import React, { useState, useEffect } from 'react';
import { ref, push, set, get } from 'firebase/database';
import { database } from '../firebase/config';
import { useUserAuth } from '../contexts/UserAuthContext';
import {
  Card,
  Button,
  Input,
  TextArea,
  FormGroup,
  HelpText,
  ErrorText,
  CheckboxGroup,
  CheckboxItem,
  RadioGroup,
  RadioItem
} from '../styles/GlobalStyles';
import styled from 'styled-components';

const WizardContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const QuestionCard = styled(Card)`
  max-width: 600px;
  width: 100%;
  position: relative;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  margin-bottom: 32px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: #0d6efd;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const QuestionNumber = styled.p`
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 600;
`;

const QuestionTitle = styled.h2`
  color: #212529;
  font-size: 24px;
  margin-bottom: 16px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const KeyboardHint = styled.p`
  color: #6c757d;
  font-size: 12px;
  text-align: center;
  margin-top: 16px;
  font-style: italic;
`;

const MultiColumnCheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  max-width: 600px;
`;

const RecommendationRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 500px;
`;



const RecommendationScale = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
  overflow-x: auto;
  
  @media (max-width: 768px) {
    gap: 2px;
    justify-content: flex-start;
  }
`;

const RecommendationOption = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 6px 2px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  min-width: 35px;
  flex-shrink: 0;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  input[type="radio"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    margin-bottom: 3px;
  }
  
  span {
    font-size: 13px;
    font-weight: 600;
    color: #495057;
    white-space: nowrap;
  }
  
  @media (max-width: 768px) {
    min-width: 30px;
    padding: 4px 1px;
    
    input[type="radio"] {
      width: 14px;
      height: 14px;
    }
    
    span {
      font-size: 12px;
    }
  }
`;

const RecommendationLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

interface Question {
  id: string;
  title: string;
  type: 'text' | 'textarea' | 'number' | 'boolean' | 'multiselect' | 'checkbox' | 'select' | 'radio';
  helpText?: string;
  options?: string[];
  required: boolean;
  dependsOn?: string;
  dependsOnValue?: any;
}

const questions: Question[] = [
  {
    id: 'atividades',
    title: 'Nos conte sobre as atividades da OSC',
    type: 'textarea',
    required: true
  },
  {
    id: 'numeroFuncionarios',
    title: 'Qual é o número de funcionários da organização?',
    type: 'number',
    helpText: 'Funcionários contratados formalmente pela organização (com vínculo empregatício)',
    required: true
  },
  {
    id: 'numeroVoluntarios',
    title: 'Qual é o número de voluntários da organização?',
    type: 'number',
    helpText: '(Pessoas sem nenhum vínculo formal com a organização)',
    required: true
  },
  {
    id: 'pessoasAtendidasMes',
    title: 'Quantas pessoas a organização atende por mês?',
    type: 'number',
    required: true
  },
  {
    id: 'suspendeAtividades',
    title: 'A organização costuma suspender suas atividades em algum período do ano (por exemplo, férias ou recesso)?',
    type: 'boolean',
    required: true
  },
  {
    id: 'mesesSuspensao',
    title: 'Indique os meses em que a organização costuma suspender suas atividades (férias, recesso etc.)',
    type: 'checkbox',
    helpText: 'Você pode selecionar mais de um mês',
    options: [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ],
    required: true,
    dependsOn: 'suspendeAtividades',
    dependsOnValue: true
  },
  {
    id: 'documentos',
    title: 'Dos documentos listados, assinale quais dessas alternativas a entidade possui',
    type: 'checkbox',
    options: [
      'Convênio com prefeituras ou outros órgãos públicos',
      'Convênio específico para alimentação',
      'Cartão CNPJ atualizado',
      'Estatuto Social com RG e CPF dos membros atualizado',
      'Ata da Posse de Assembleia com RG e CPF dos membros atualizado',
      'Alvará Sanitário vigente'
    ],
    required: true
  },
  {
    id: 'publicoAtendido',
    title: 'Selecione o público que a instituição atende.',
    type: 'checkbox',
    helpText: '(Você pode selecionar mais de uma opção.)',
    options: [
      'Afrodescendentes', 'Crianças', 'Famílias em vulnerabilidade', 'Idosos',
      'Imigrantes', 'Indígenas', 'Mães solo/solteiras', 'Mulheres',
      'Pessoas com câncer', 'Pessoas com deficiência', 'Pessoas com dependência química',
      'Pessoas com HIV', 'Pessoas em situação de rua', 'Pessoas LGBTQIAPNB+',
      'Pessoas vítimas de violência', 'Quilombolas', 'Refugiados', 'Outros'
    ],
    required: true
  },
  {
    id: 'publicoAtendidoOutros',
    title: 'Especifique outros públicos atendidos:',
    type: 'text',
    required: true,
    dependsOn: 'publicoAtendido',
    dependsOnValue: 'Outros'
  },
  {
    id: 'criancas',
    title: 'Quantas Crianças (0 a 12 anos) a organização atende?',
    type: 'number',
    required: true
  },
  {
    id: 'adolescentes',
    title: 'Quantos Adolescentes (13 a 17 anos) a organização atende?',
    type: 'number',
    required: true
  },
  {
    id: 'jovens',
    title: 'Quantos Jovens (18 a 29 anos) a organização atende?',
    type: 'number',
    required: true
  },
  {
    id: 'adultos',
    title: 'Quantos Adultos (30 a 59 anos) a organização atende?',
    type: 'number',
    required: true
  },
  {
    id: 'idosos',
    title: 'Quantas Pessoas Idosas (acima dos 60 anos) a organização atende?',
    type: 'number',
    required: true
  },
  {
          id: 'possuiFichaCadastral',
          title: 'Possui ficha cadastral com dados socioeconômicos dos atendidos que participam das ações sociais e/ou recebem doações da OSC?',
    type: 'boolean',
    helpText: 'Dados socioenômicos - Exemplos: Nome, Idade, etnia, sexo, renda média da família, número de pessoas na família, etc…',
    required: true
  },
  {
    id: 'beneficiariosMensais',
    title: 'Quantas pessoas se beneficiam mensalmente dos alimentos fornecidos pela sua OSC?',
    type: 'number',
    required: true
  },
  {
    id: 'infraestrutura',
    title: 'Assinale quais destas alternativas a entidade possui',
    type: 'checkbox',
    options: [
      'Cozinha',
      'Espaço adequado para armazenamento de alimentos',
      'Profissionais com formação em Nutrição e/ou Gastronomia',
      'Refeitório',
      'Sistema de refrigeração (geladeira e/ou freezer)'
    ],
    required: true
  },
  {
    id: 'serveRefeicoes',
    title: 'Servem refeições no local?',
    type: 'boolean',
    required: true
  },
  {
    id: 'serveCafeManha',
    title: 'Servem Café da manhã?',
    type: 'boolean',
    required: true,
    dependsOn: 'serveRefeicoes',
    dependsOnValue: true
  },
  {
    id: 'pessoasCafeManha',
    title: 'Pra quantas pessoas o café da manhã é servido ao longo do mês?',
    type: 'number',
    required: true,
    dependsOn: 'serveCafeManha',
    dependsOnValue: true
  },
  {
    id: 'diasCafeManha',
    title: 'Quantos dias por semana é servido o café da manhã?',
    type: 'number',
    helpText: 'Digite um valor entre 1 e 7',
    required: true,
    dependsOn: 'serveCafeManha',
    dependsOnValue: true
  },
  {
    id: 'serveLancheManha',
    title: 'Servem Lanche da manhã?',
    type: 'boolean',
    required: true,
    dependsOn: 'serveRefeicoes',
    dependsOnValue: true
  },
  {
    id: 'pessoasLancheManha',
    title: 'Pra quantas pessoas o lanche da manhã é servido ao longo do mês?',
    type: 'number',
    required: true,
    dependsOn: 'serveLancheManha',
    dependsOnValue: true
  },
  {
    id: 'diasLancheManha',
    title: 'Quantos dias por semana é servido o lanche da manhã?',
    type: 'number',
    helpText: 'Digite um valor entre 1 e 7',
    required: true,
    dependsOn: 'serveLancheManha',
    dependsOnValue: true
  },
  {
    id: 'serveAlmoco',
    title: 'Servem Almoço?',
    type: 'boolean',
    required: true,
    dependsOn: 'serveRefeicoes',
    dependsOnValue: true
  },
  {
    id: 'pessoasAlmoco',
    title: 'Pra quantas pessoas o almoço é servido ao longo do mês?',
    type: 'number',
    required: true,
    dependsOn: 'serveAlmoco',
    dependsOnValue: true
  },
  {
    id: 'diasAlmoco',
    title: 'Quantos dias por semana é servido o almoço?',
    type: 'number',
    helpText: 'Digite um valor entre 1 e 7',
    required: true,
    dependsOn: 'serveAlmoco',
    dependsOnValue: true
  },
  {
    id: 'serveLancheTarde',
    title: 'Servem Lanche da Tarde?',
    type: 'boolean',
    required: true,
    dependsOn: 'serveRefeicoes',
    dependsOnValue: true
  },
  {
    id: 'pessoasLancheTarde',
    title: 'Pra quantas pessoas o lanche da tarde é servido ao longo do mês?',
    type: 'number',
    required: true,
    dependsOn: 'serveLancheTarde',
    dependsOnValue: true
  },
  {
    id: 'diasLancheTarde',
    title: 'Quantos dias por semana é servido o lanche da tarde?',
    type: 'number',
    helpText: 'Digite um valor entre 1 e 7',
    required: true,
    dependsOn: 'serveLancheTarde',
    dependsOnValue: true
  },
  {
    id: 'serveJantar',
    title: 'Servem Jantar?',
    type: 'boolean',
    required: true,
    dependsOn: 'serveRefeicoes',
    dependsOnValue: true
  },
  {
    id: 'pessoasJantar',
    title: 'Pra quantas pessoas o jantar é servido ao longo do mês?',
    type: 'number',
    required: true,
    dependsOn: 'serveJantar',
    dependsOnValue: true
  },
  {
    id: 'diasJantar',
    title: 'Quantos dias por semana é servido o jantar?',
    type: 'number',
    helpText: 'Digite um valor entre 1 e 7',
    required: true,
    dependsOn: 'serveJantar',
    dependsOnValue: true
  },
  {
    id: 'distribuiAlimentos',
    title: 'Sua organização distribui alimentos para pessoas em situação de vulnerabilidade?',
    type: 'boolean',
    required: true
  },
  {
    id: 'distribuiCestasBasicas',
    title: 'Sua organização realiza distribuição de Cestas Básicas?',
    type: 'boolean',
    required: true,
    dependsOn: 'distribuiAlimentos',
    dependsOnValue: true
  },
  {
    id: 'quantidadeCestasBasicas',
    title: 'Qual a quantidade total distribuída em média por mês de Cestas Básicas em sua organização?',
    type: 'number',
    required: true,
    dependsOn: 'distribuiCestasBasicas',
    dependsOnValue: true
  },
  {
    id: 'distribuiMarmitas',
    title: 'Sua organização realiza distribuição de Marmitas/ Quentinhas?',
    type: 'boolean',
    required: true,
    dependsOn: 'distribuiAlimentos',
    dependsOnValue: true
  },
  {
    id: 'quantidadeMarmitas',
    title: 'Qual a quantidade total distribuída em média por mês de Marmitas/ Quentinhas em sua organização?',
    type: 'number',
    required: true,
    dependsOn: 'distribuiMarmitas',
    dependsOnValue: true
  },
  {
    id: 'distribuiKitFrutas',
    title: 'Sua organização realiza distribuição de Kit/ Sacolinha de frutas, legumes, verduras?',
    type: 'boolean',
    required: true,
    dependsOn: 'distribuiAlimentos',
    dependsOnValue: true
  },
  {
    id: 'quantidadeKitFrutas',
    title: 'Qual a quantidade total distribuída em média por mês de Kit/ Sacolinha de frutas, legumes, verduras em sua organização?',
    type: 'number',
    required: true,
    dependsOn: 'distribuiKitFrutas',
    dependsOnValue: true
  },
  {
    id: 'atividadesAlimentacao',
    title: 'Realizam alguma atividade voltada para a alimentação com os atendidos (oficinas, aulas, etc)?',
    type: 'boolean',
    required: true
  },
  {
    id: 'tiposAtividadesAlimentacao',
    title: 'Quais atividades voltadas para a alimentação são realizadas?',
    type: 'checkbox',
    helpText: '(Mais de uma alternativa pode ser selecionada)',
    options: [
      'Cursos Profissionalizantes',
      'Oficinas',
      'Palestras',
      'Outros'
    ],
    required: true,
    dependsOn: 'atividadesAlimentacao',
    dependsOnValue: true
  },
  {
    id: 'outrasAtividadesAlimentacao',
    title: 'Especifique outras atividades:',
    type: 'text',
    required: true,
    dependsOn: 'tiposAtividadesAlimentacao',
    dependsOnValue: 'Outros'
  },
  {
    id: 'descricaoAtividadesAlimentacao',
    title: 'Por favor, conte-nos um pouco sobre as atividades realizadas',
    type: 'textarea',
    required: true,
    dependsOn: 'atividadesAlimentacao',
    dependsOnValue: true
  },
  {
    id: 'transfereDoacoes',
    title: 'Em caso de recebimento de doações, existe a intenção de transferi-las para outra unidade ou filial da organização?',
    type: 'boolean',
    required: true
  },
  {
    id: 'unidadesBeneficiadas',
    title: 'Por favor, liste as unidades ou filiais beneficiadas e seus respectivos endereços.',
    type: 'textarea',
    required: true,
    dependsOn: 'transfereDoacoes',
    dependsOnValue: true
  },
  {
    id: 'restricoesColeta',
    title: 'Possui algum tipo de restrição para coletar doações?',
    type: 'boolean',
    required: true
  },
  {
    id: 'descricaoRestricoes',
    title: 'Por favor, descreva as restrições para coletar doações no campo abaixo.',
    type: 'textarea',
    required: true,
    dependsOn: 'restricoesColeta',
    dependsOnValue: true
  },
  {
    id: 'disponibilidadeColeta',
    title: 'Qual seria a disponibilidade da instituição para coletar as doações?',
    type: 'radio',
    options: [
      '1 vez por semana',
      '2 vezes por semana',
      '3 vezes por semana',
      '4 vezes por semana',
      '5 vezes por semana',
      '6 vezes por semana',
      'Todos os dias'
    ],
    required: true
  },
  {
    id: 'recebeDoacoesOutrosParceiros',
    title: 'Recebem doações de alimentos de outros parceiros?',
    type: 'boolean',
    required: true
  },
  {
    id: 'tiposAlimentosRecebidos',
    title: 'Selecione os tipos de alimento que a instituição recebe de outros parceiros',
    type: 'checkbox',
    helpText: '(mais de uma alternativa pode ser selecionada)',
    options: [
      'Frutas',
      'Legumes',
      'Verduras',
      'Mercearia (Arroz, Feijão, Café, etc...)',
      'Carnes',
      'Pães',
      'Outros'
    ],
    required: true,
    dependsOn: 'recebeDoacoesOutrosParceiros',
    dependsOnValue: true
  },
  {
    id: 'outrosTiposAlimentos',
    title: 'Especifique outros tipos de alimentos:',
    type: 'text',
    required: true,
    dependsOn: 'tiposAlimentosRecebidos',
    dependsOnValue: 'Outros'
  },
  {
    id: 'recebeBancoAlimentos',
    title: 'Recebe doação de Banco de Alimentos?',
    type: 'boolean',
    required: true,
    dependsOn: 'recebeDoacoesOutrosParceiros',
    dependsOnValue: true
  },
  {
    id: 'quaisBancosAlimentos',
    title: 'De quais Banco de Alimentos recebem doação?',
    type: 'textarea',
    required: true,
    dependsOn: 'recebeBancoAlimentos',
    dependsOnValue: true
  },
  {
    id: 'recebeSescMesaBrasil',
    title: 'Recebe doação do Sesc Mesa Brasil?',
    type: 'boolean',
    required: true,
    dependsOn: 'recebeDoacoesOutrosParceiros',
    dependsOnValue: true
  },
  {
    id: 'unidadesSescMesaBrasil',
    title: 'De quais unidades do Sesc Mesa Brasil recebem doações?',
    type: 'textarea',
    required: true,
    dependsOn: 'recebeSescMesaBrasil',
    dependsOnValue: true
  },
  {
    id: 'recebeDoacoesGoverno',
    title: 'Recebe doação de Prefeitura/ Governo Estadual / Governo Federal?',
    type: 'boolean',
    required: true,
    dependsOn: 'recebeDoacoesOutrosParceiros',
    dependsOnValue: true
  },
  {
    id: 'recebeDoacoesPessoasFisicas',
    title: 'Recebe doação de pessoas físicas?',
    type: 'boolean',
    required: true,
    dependsOn: 'recebeDoacoesOutrosParceiros',
    dependsOnValue: true
  },
  {
    id: 'frequenciaDoacoesOutrosParceiros',
    title: 'Selecione a frequência com que a organização recebe doação de outros parceiros',
    type: 'radio',
    options: [
      '1 vez por semana',
      '2 vezes por semana',
      '3 vezes por semana',
      '4 vezes por semana',
      '5 vezes por semana',
      '6 vezes por semana',
      'Todos os dias',
      'Uma vez a cada 15 dias',
      'Uma vez por mês',
      'Não possui frequência determinada'
    ],
    required: true,
    dependsOn: 'recebeDoacoesOutrosParceiros',
    dependsOnValue: true
  },
  {
    id: 'possuiTransporte',
    title: 'Possui transporte para retirar as doações?',
    type: 'boolean',
    required: true
  },
  {
    id: 'tipoTransporte',
    title: 'Especifique se o transporte é próprio ou de terceiros',
    type: 'radio',
    options: [
      'Transporte próprio',
      'Transporte de terceiros'
    ],
    required: true,
    dependsOn: 'possuiTransporte',
    dependsOnValue: true
  },
  {
    id: 'veiculoColeta',
    title: 'Que veículo (Tipo, marca) seria utilizado para coleta?',
    type: 'textarea',
    required: true
  },
  {
    id: 'avaliacaoFormulario',
    title: 'O que você achou da experiência deste formulário?',
    type: 'radio',
    options: [
      '⭐ (1 estrela)',
      '⭐⭐ (2 estrelas)',
      '⭐⭐⭐ (3 estrelas)',
      '⭐⭐⭐⭐ (4 estrelas)',
      '⭐⭐⭐⭐⭐ (5 estrelas)'
    ],
    required: true
  },
  {
    id: 'comentarioExperiencia',
    title: 'Deixe um comentário sobre sua experiência',
    type: 'textarea',
    required: false
  },
  {
    id: 'probabilidadeRecomendacao',
    title: 'Qual a probabilidade de você nos recomendar?',
    type: 'radio',
    helpText: '0 é "Nada provável" e 10 é "Extremamente provável"',
    options: [
      '0 - Nada provável',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10 - Extremamente provável'
    ],
    required: true
  }
];

interface FormWizardProps {
  onComplete: () => void;
  onCancel: () => void;
  initialData?: Record<string, any> | null;
}

const clearPartialResponse = async (userId: string) => {
  // Limpar do localStorage
  localStorage.removeItem(`partial_response_${userId}`);
  
  // Limpar do Firebase
  try {
    const partialResponseRef = ref(database, `partialResponses/${userId}`);
    await set(partialResponseRef, null);
  } catch (error) {
    
  }
};

const FormWizard: React.FC<FormWizardProps> = ({ onComplete, onCancel, initialData }) => {
  const { currentUser } = useUserAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPartialResponse, setIsPartialResponse] = useState(false);
  const [isPrefilledFromPrevious, setIsPrefilledFromPrevious] = useState(false);

  // Carregar resposta parcial salva ou dados iniciais ao inicializar
  useEffect(() => {
    const loadPartialResponse = async () => {
      if (currentUser) {
        
        
        // Primeiro, tentar carregar do Firebase
        try {
          const partialResponseRef = ref(database, `partialResponses/${currentUser.uid}`);
          const snapshot = await get(partialResponseRef);
          
          if (snapshot.exists()) {
            const firebasePartialResponse = snapshot.val();
            
            setAnswers(firebasePartialResponse.answers || {});
            setCurrentQuestionIndex(firebasePartialResponse.currentQuestionIndex || 0);
            setIsPartialResponse(true);
            
            // Sincronizar com localStorage para backup
            localStorage.setItem(`partial_response_${currentUser.uid}`, JSON.stringify(firebasePartialResponse));
            return;
          }
        } catch (error) {

        }
        
        // Se não encontrou no Firebase, tentar localStorage como fallback
        const savedPartialResponse = localStorage.getItem(`partial_response_${currentUser.uid}`);
        if (savedPartialResponse) {

          try {
            const parsedResponse = JSON.parse(savedPartialResponse);

            setAnswers(parsedResponse.answers || {});
            setCurrentQuestionIndex(parsedResponse.currentQuestionIndex || 0);
            setIsPartialResponse(true);
            
            // Sincronizar com Firebase
            try {
              const partialResponseRef = ref(database, `partialResponses/${currentUser.uid}`);
              await set(partialResponseRef, parsedResponse);
            } catch (error) {

            }
            return;
          } catch (error) {

          }
        }
        
        // Se não há resposta parcial, usar dados da resposta mais recente
        if (initialData && Object.keys(initialData).length > 0) {

          
          // Filtrar propriedades undefined
          const cleanedData = Object.fromEntries(
            Object.entries(initialData).filter(([key, value]) => value !== undefined)
          );

          
          setAnswers(cleanedData);
          setCurrentQuestionIndex(0);
          setIsPartialResponse(false);
          setIsPrefilledFromPrevious(true);
        } else {

        }
      }
    };
    
    loadPartialResponse();
  }, [currentUser, initialData]);

  // Função para filtrar dados de perguntas dependentes
  const filterDependentAnswers = (answers: Record<string, any>) => {
    const filteredAnswers = { ...answers };
    
    // Para cada pergunta, verificar se ela depende de outra
    questions.forEach(question => {
      if (question.dependsOn) {
        const dependentValue = answers[question.dependsOn];
        
        // Se a pergunta dependente não atende à condição, remover a resposta
        if (question.dependsOnValue === true || question.dependsOnValue === false) {
          if (dependentValue !== question.dependsOnValue) {
            delete filteredAnswers[question.id];
          }
        } else if (Array.isArray(dependentValue)) {
          if (!dependentValue.includes(question.dependsOnValue)) {
            delete filteredAnswers[question.id];
          }
        } else {
          if (dependentValue !== question.dependsOnValue) {
            delete filteredAnswers[question.id];
          }
        }
      }
    });
    
    return filteredAnswers;
  };

  // Salvar resposta parcial automaticamente
  useEffect(() => {
    const savePartialResponse = async () => {
      if (currentUser && Object.keys(answers).length > 0) {
        // Filtrar dados de perguntas dependentes antes de salvar parcialmente
        const filteredAnswers = filterDependentAnswers(answers);
        
        const partialResponse = {
          answers: filteredAnswers,
          currentQuestionIndex,
          lastSaved: new Date().toISOString()
        };
        
        // Salvar no localStorage (backup local)
        localStorage.setItem(`partial_response_${currentUser.uid}`, JSON.stringify(partialResponse));
        
        // Salvar no Firebase
        try {
          const partialResponseRef = ref(database, `partialResponses/${currentUser.uid}`);
          await set(partialResponseRef, partialResponse);

        } catch (error) {

        }
      }
    };
    
    savePartialResponse();
  }, [answers, currentQuestionIndex, currentUser]);

  const visibleQuestions = questions.filter(question => {
    if (!question.dependsOn) return true;
    
    const dependentValue = answers[question.dependsOn];
    if (question.dependsOnValue === true || question.dependsOnValue === false) {
      return dependentValue === question.dependsOnValue;
    }
    
    if (Array.isArray(dependentValue)) {
      return dependentValue.includes(question.dependsOnValue);
    }
    
    return dependentValue === question.dependsOnValue;
  });

  const currentQuestion = visibleQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / visibleQuestions.length) * 100;

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      
      // Para campos de texto, permitir Enter para avançar
      if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'text') {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleNext();
        }
        return;
      }
      
      // Para textarea, usar Shift+Enter para avançar
      if (target.tagName === 'TEXTAREA') {
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault();
          handleNext();
        }
        return;
      }
      
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleNext();
        return;
      }
      
      // Atalhos para perguntas de sim/não
      if (currentQuestion.type === 'boolean') {
        if (e.key.toLowerCase() === 's') {
          e.preventDefault();
          handleAnswerChange(true);
        } else if (e.key.toLowerCase() === 'n') {
          e.preventDefault();
          handleAnswerChange(false);
        }
        return;
      }
      
      // Atalhos para perguntas de múltipla escolha (checkbox)
      if (currentQuestion.type === 'checkbox' && currentQuestion.options) {
        const keyPressed = e.key.toLowerCase();
        const optionIndex = keyPressed.charCodeAt(0) - 97; // 'a' = 0, 'b' = 1, etc.
        
        if (optionIndex >= 0 && optionIndex < currentQuestion.options.length) {
          e.preventDefault();
          const option = currentQuestion.options[optionIndex];
          const currentValues = answers[currentQuestion.id] || [];
          const isChecked = currentValues.includes(option);
          handleCheckboxChange(option, !isChecked);
        }
      }
      
      // Atalhos para perguntas de escolha única (radio)
      if (currentQuestion.type === 'radio' && currentQuestion.options) {
        const keyPressed = e.key.toLowerCase();
        const optionIndex = keyPressed.charCodeAt(0) - 97; // 'a' = 0, 'b' = 1, etc.
        
        if (optionIndex >= 0 && optionIndex < currentQuestion.options.length) {
          e.preventDefault();
          const option = currentQuestion.options[optionIndex];
          handleAnswerChange(option);
        }
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [currentQuestionIndex, answers, currentQuestion]);

  const handleAnswerChange = (value: any) => {
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
    setError('');
  };

  const handleCheckboxChange = (option: string, checked: boolean) => {
    const currentValues = answers[currentQuestion.id] || [];
    let newValues;
    
    if (checked) {
      newValues = [...currentValues, option];
    } else {
      newValues = currentValues.filter((v: string) => v !== option);
    }
    
    handleAnswerChange(newValues);
  };

  const validateCurrentAnswer = () => {
    if (!currentQuestion.required) {
      
      return true;
    }
    
    const answer = answers[currentQuestion.id];
    
    
    if (currentQuestion.type === 'number') {
      if (answer === undefined || answer === null || answer === '') {
        
        setError('Esta pergunta é obrigatória');
        return false;
      }
      
      if (isNaN(Number(answer))) {
        
        setError('Esta pergunta é obrigatória');
        return false;
      }
      
      const numValue = Number(answer);
      
      
      // Para perguntas de dias da semana, validar entre 1 e 7
      if (currentQuestion.id.includes('dias')) {
        if (numValue < 1 || numValue > 7) {
          
          setError('Valor deve estar entre 1 e 7 dias');
          return false;
        }
      } else {
        // Perguntas que podem aceitar 0 como resposta válida
        const questionsAllowingZero = [
          'numeroFuncionarios',
          'numeroVoluntarios', 
          'criancas',
          'adolescentes',
          'jovens',
          'adultos',
          'idosos'
        ];
        
        if (questionsAllowingZero.includes(currentQuestion.id)) {
          // Para essas perguntas, aceitar 0 ou valores positivos
          if (numValue < 0) {
            
            setError('Valor deve ser maior ou igual a zero');
            return false;
          }
          
          // Validação especial para numeroVoluntarios: soma funcionários + voluntários deve ser > 0
          if (currentQuestion.id === 'numeroVoluntarios') {
            const numeroFuncionarios = Number(answers['numeroFuncionarios']) || 0;
            const numeroVoluntarios = numValue;
            const soma = numeroFuncionarios + numeroVoluntarios;
            
            
            
            if (soma <= 0) {
              
              setError('O Número de Funcionarios + Voluntários deve ser maior que 0');
              return false;
            }
          }
        } else {
          // Para outras perguntas numéricas, não aceitar valores <= 0
          if (numValue <= 0) {
            
            setError('Valor deve ser maior que zero');
            return false;
          }
        }
      }
      
      
      return true;
    }
    
    if (answer === undefined || answer === null || answer === '') {
      
      setError('Esta pergunta é obrigatória');
      return false;
    }
    
    if (Array.isArray(answer) && answer.length === 0) {
      
      setError('Selecione pelo menos uma opção');
      return false;
    }
    
    
    return true;
  };

  const handleNext = () => {
    const isValid = validateCurrentAnswer();
    
    if (!isValid) {
      return;
    }
    
    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      
      return;
    }
    
    
    
    
    // Filtrar dados de perguntas dependentes
    const filteredAnswers = filterDependentAnswers(answers);
    
    
    
    
    
    setLoading(true);
    try {
      const formResponse = {
        oscId: currentUser.uid,
        createdAt: new Date().toISOString(),
        ...filteredAnswers
      };
      
      
      
      
      // Verificar se há propriedades undefined que podem causar problemas
      const undefinedProps = Object.entries(formResponse).filter(([key, value]) => value === undefined);
      if (undefinedProps.length > 0) {
        
      }
      
      const dbRef = ref(database, 'responses');
      
      
      await push(dbRef, formResponse);
      
      
      
      // Limpar resposta parcial do localStorage e Firebase após envio bem-sucedido
      await clearPartialResponse(currentUser.uid);
      
      onComplete();
    } catch (error) {
      
      
      
      if (error instanceof Error) {
        
        
      }
      setError('Erro ao salvar resposta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderInput = () => {
    const currentValue = answers[currentQuestion.id];
    
    switch (currentQuestion.type) {
      case 'textarea':
        return (
          <TextArea
            value={currentValue || ''}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder="Digite sua resposta..."
            autoFocus
          />
        );
        
      case 'number':
        return (
          <Input
            type="number"
            value={currentValue || ''}
            onChange={(e) => handleAnswerChange(parseInt(e.target.value) || 0)}
            placeholder="0"
            min="0"
            autoFocus
          />
        );
        
      case 'text':
        return (
          <Input
            type="text"
            value={currentValue || ''}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder="Digite sua resposta..."
            autoFocus
          />
        );
        
      case 'boolean':
        return (
          <RadioGroup>
            <RadioItem>
              <input
                type="radio"
                name={currentQuestion.id}
                checked={currentValue === true}
                onChange={() => handleAnswerChange(true)}
              />
              <span>Sim <kbd style={{ marginLeft: '8px', padding: '2px 6px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '12px' }}>S</kbd></span>
            </RadioItem>
            <RadioItem>
              <input
                type="radio"
                name={currentQuestion.id}
                checked={currentValue === false}
                onChange={() => handleAnswerChange(false)}
              />
              <span>Não <kbd style={{ marginLeft: '8px', padding: '2px 6px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '12px' }}>N</kbd></span>
            </RadioItem>
          </RadioGroup>
        );
        
      case 'checkbox':
        // Para as perguntas 6 (mesesSuspensao), 7 (documentos) e 8 (publicoAtendido), usar layout em colunas
        const useMultiColumn = currentQuestion.id === 'publicoAtendido' || currentQuestion.id === 'documentos' || currentQuestion.id === 'mesesSuspensao';
        const CheckboxContainer = useMultiColumn ? MultiColumnCheckboxGroup : CheckboxGroup;
        
        return (
          <CheckboxContainer>
            {currentQuestion.options?.map((option, index) => {
              const letter = String.fromCharCode(97 + index); // 'a', 'b', 'c', etc.
              return (
                <CheckboxItem key={option}>
                  <input
                    type="checkbox"
                    checked={(currentValue || []).includes(option)}
                    onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                  />
                  <span>
                    {option} 
                    <kbd style={{ marginLeft: '8px', padding: '2px 6px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '12px' }}>
                      {letter.toUpperCase()}
                    </kbd>
                  </span>
                </CheckboxItem>
              );
            })}
          </CheckboxContainer>
        );
        
      case 'radio':
        // Layout especial para a pergunta de recomendação
        if (currentQuestion.id === 'probabilidadeRecomendacao') {
          return (
            <RecommendationRadioGroup>
              <RecommendationScale>
                {currentQuestion.options?.map((option, index) => {
                  const letter = String.fromCharCode(97 + index); // 'a', 'b', 'c', etc.
                  // Extrai apenas o número da opção (0, 1, 2, etc.)
                  let number;
                  if (option.includes(' - ')) {
                    number = option.split(' - ')[0]; // Para "0 - Nada provável" e "10 - Extremamente provável"
                  } else {
                    number = option; // Para "1", "2", "3", etc.
                  }
                  return (
                    <RecommendationOption key={option}>
                      <input
                        type="radio"
                        name={currentQuestion.id}
                        checked={currentValue === option}
                        onChange={() => handleAnswerChange(option)}
                      />
                      <span>{number}</span>
                      <kbd style={{ padding: '1px 4px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '10px', marginTop: '2px' }}>
                        {letter.toUpperCase()}
                      </kbd>
                    </RecommendationOption>
                  );
                })}
              </RecommendationScale>
              <RecommendationLabels>
                <span>Nada provável</span>
                <span>Extremamente provável</span>
              </RecommendationLabels>
            </RecommendationRadioGroup>
          );
        }
        
        // Layout padrão para outras perguntas radio
        return (
          <RadioGroup>
            {currentQuestion.options?.map((option, index) => {
              const letter = String.fromCharCode(97 + index); // 'a', 'b', 'c', etc.
              return (
                <RadioItem key={option}>
                  <input
                    type="radio"
                    name={currentQuestion.id}
                    checked={currentValue === option}
                    onChange={() => handleAnswerChange(option)}
                  />
                  <span>
                    {option}
                    <kbd style={{ marginLeft: '8px', padding: '2px 6px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '3px', fontSize: '12px' }}>
                      {letter.toUpperCase()}
                    </kbd>
                  </span>
                </RadioItem>
              );
            })}
          </RadioGroup>
        );
        
      default:
        return null;
    }
  };

  return (
    <WizardContainer>
      <QuestionCard>
        {isPartialResponse && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '20px',
            color: '#856404',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            📝 Continuando resposta salva automaticamente
          </div>
        )}
        
        {isPrefilledFromPrevious && (
          <div style={{
            background: '#d1ecf1',
            border: '1px solid #bee5eb',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '20px',
            color: '#0c5460',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            ℹ️ Formulário pré-preenchido com dados da sua resposta mais recente. Você pode alterar qualquer informação.
          </div>
        )}
        
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>
        
        <QuestionNumber>
          Pergunta {currentQuestionIndex + 1} de {visibleQuestions.length}
        </QuestionNumber>
        
        <QuestionTitle>{currentQuestion.title}</QuestionTitle>
        
        {currentQuestion.helpText && (
          <HelpText>{currentQuestion.helpText}</HelpText>
        )}
        
        <FormGroup>
          {renderInput()}
        </FormGroup>
        
        {error && <ErrorText>{error}</ErrorText>}
        
        <NavigationButtons>
          <div style={{ display: 'flex', gap: '8px' }}>
            {currentQuestionIndex > 0 && (
              <Button variant="secondary" onClick={handlePrevious}>
                Anterior
              </Button>
            )}
            {isPartialResponse && (
              <Button 
                variant="secondary" 
                onClick={async () => {
                  if (currentUser && window.confirm('Deseja começar uma nova resposta do zero? Isso apagará o progresso atual.')) {
                    await clearPartialResponse(currentUser.uid);
                    setAnswers({});
                    setCurrentQuestionIndex(0);
                    setIsPartialResponse(false);
                  }
                }}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                Começar do Zero
              </Button>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button variant="danger" onClick={onCancel}>
              Sair
            </Button>
            <Button onClick={handleNext} disabled={loading}>
              {loading ? 'Salvando...' : 
               currentQuestionIndex === visibleQuestions.length - 1 ? 'Finalizar' : 'Próxima'}
            </Button>
          </div>
        </NavigationButtons>
        
        <KeyboardHint>
          {currentQuestion.type === 'boolean' ? (
            <>Pressione S para Sim, N para Não, ou Enter para avançar</>
          ) : currentQuestion.type === 'checkbox' ? (
            <>Pressione A-{String.fromCharCode(96 + (currentQuestion.options?.length || 0)).toUpperCase()} para selecionar opções, ou Enter para avançar</>
          ) : currentQuestion.type === 'radio' ? (
            <>Pressione A-{String.fromCharCode(96 + (currentQuestion.options?.length || 0)).toUpperCase()} para selecionar uma opção, ou Enter para avançar</>
          ) : currentQuestion.type === 'textarea' ? (
            <>Pressione Shift+Enter para avançar</>
          ) : (
            <>Pressione Enter para avançar</>
          )}
        </KeyboardHint>
      </QuestionCard>
    </WizardContainer>
  );
};

export default FormWizard;